import * as yup from 'yup';
import { Alert, Card, CardBody, Col, Form, FormGroup, Row } from 'reactstrap';
import { NavLink, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../store';

import Button from '../../components/Button';
import Input from '../../components/Input';
import Logo from '../../assets/images/logo-blue.png';
import React, { useCallback } from 'react';
import { requestPasswordReset } from '../../store/auth/Actions';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Form data interface
interface PasswordResetRequestFormData {
    username: string;
}

// Validation schema
const passwordResetRequestSchema = yup.object({
    username: yup.string().required('Email/Username is required').email('Please enter a valid email'),
});

const PasswordResetRequest = () => {
    const auth = useSelector((state: any) => state.auth);
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
    } = useForm<PasswordResetRequestFormData>({
        resolver: yupResolver(passwordResetRequestSchema),
        mode: 'onChange',
        defaultValues: {
            username: '',
        },
    });
    const dispatch = useDispatch<AppDispatch>();

    const onSubmit = useCallback(
        (formData: PasswordResetRequestFormData) => {
            dispatch(requestPasswordReset(formData));
        },
        [dispatch],
    );

    return (
        <>
            {auth.authenticated && <Redirect to="/profile" />}
            <Row className="justify-content-center">
                <Col md="8" lg="6" xl="5">
                    <Card>
                        <CardBody>
                            <div className="text-center w-75 m-auto">
                                <NavLink to="/">
                                    <span>
                                        <img src={Logo} alt="logo" height="150" />
                                    </span>
                                </NavLink>
                                <p className="text-muted mb-4 mt-3">
                                    Enter your email to receive an email with a link to reset your password.
                                </p>
                            </div>

                            {auth.passwordReset && (
                                <Alert color={'success'} transition={{ timeout: 0 }}>
                                    An email has been sent with instructions to complete your password reset. Please
                                    click the button in the email to complete the password reset process.
                                </Alert>
                            )}

                            {!auth.passwordReset && auth.passwordResetError && (
                                <Alert color={'danger'} transition={{ timeout: 0 }}>
                                    {auth.passwordResetError}
                                </Alert>
                            )}

                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <FormGroup className="mb-3">
                                    <label htmlFor="username">Email address</label>
                                    <Controller
                                        name="username"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                className="form-control"
                                                type="email"
                                                id="username"
                                                placeholder="Enter your email / username"
                                            />
                                        )}
                                    />
                                    {errors.username && <span className="text-danger">{errors.username.message}</span>}
                                </FormGroup>
                                <FormGroup className="mb-0">
                                    <Button className="btn-block w-100" type="submit">
                                        Send Password Reset Email
                                    </Button>
                                </FormGroup>
                            </Form>
                        </CardBody>
                    </Card>

                    <Row className="mt-3">
                        <Col className="text-center">
                            <p>
                                <NavLink to="/login" className="ml-1">
                                    Back to Login
                                </NavLink>
                            </p>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
};

export default PasswordResetRequest;
